import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { ProjectRFQLine } from 'features/RequestForQuotations/ProjectRFQ/Components/ProjectRFQLine'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useMemo, useState } from 'react'
import {
  ProjectSupplierDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles({ name: 'ProjectRFQLines' })((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    flex: 1,
    overflow: 'auto',
  },
  bulkAssign: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    cursor: 'pointer',
    userSelect: 'none',
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  expandIcon: {
    transition: 'transform 200ms',
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
  groupActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  changeSupplierInput: {
    minWidth: 220,
  },
}))

export type RFQLine = {
  bomItemPointer?: BomItemPointer
  activities?: {
    activityId?: string
    workingStepType?: WorkingStepType
    selected?: boolean
    quantityBreaks?: number[]
  }[]
  supplier?: ProjectSupplierDto
}

type SupplierGroup = {
  supplierId: string | null
  supplier: ProjectSupplierDto | null
  lines: RFQLine[]
}

function groupLinesBySupplier(lines: RFQLine[]): SupplierGroup[] {
  const groups = new Map<string | null, RFQLine[]>()

  for (const line of lines) {
    const key = line.supplier?.supplierId ?? null
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key)!.push(line)
  }

  const result: SupplierGroup[] = []

  // unassigned first
  const unassigned = groups.get(null)
  if (unassigned) {
    result.push({ supplierId: null, supplier: null, lines: unassigned })
    groups.delete(null)
  }

  // then supplier groups
  for (const [supplierId, groupLines] of groups) {
    result.push({
      supplierId,
      supplier: groupLines[0]?.supplier ?? null,
      lines: groupLines,
    })
  }

  return result
}

type ProjectRFQLinesProps = {
  rfqLines: RFQLine[]
  onSelectionChange?: (bomItemId: string, checked: boolean) => void
  selectedBomItemIds?: Set<string>
  availableSuppliers?: ProjectSupplierDto[]
  onBulkAssignSupplier?: (supplier: ProjectSupplierDto) => void
  onChangeGroupSupplier?: (
    oldSupplierId: string | null,
    newSupplier: ProjectSupplierDto,
  ) => void
  onLineSupplierChange?: (
    bomItemId: string,
    supplier: ProjectSupplierDto | null,
  ) => void
  onLineActivitySelectionChange?: (
    bomItemId: string,
    activityIndex: number,
    selected: boolean,
  ) => void
  onUnassignAll?: () => void
  onDeleteSelected?: () => void
  selectionCount?: number
  showSupplierAssignment?: boolean
}

export const ProjectRFQLines = ({
  rfqLines,
  onSelectionChange,
  selectedBomItemIds,
  availableSuppliers,
  onBulkAssignSupplier,
  onChangeGroupSupplier,
  onLineSupplierChange,
  onLineActivitySelectionChange,
  onUnassignAll,
  onDeleteSelected,
  selectionCount,
  showSupplierAssignment = true,
}: ProjectRFQLinesProps) => {
  const { classes, cx } = useStyles()
  const getLocalizedWorkingStepName = useLocalizedWorkingStepLabel()

  const groups = useMemo(
    () =>
      showSupplierAssignment
        ? groupLinesBySupplier(rfqLines)
        : [{ supplierId: null, supplier: null, lines: rfqLines }],
    [rfqLines, showSupplierAssignment],
  )

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string | null>>(
    new Set(),
  )
  const [changingSupplierFor, setChangingSupplierFor] = useState<
    string | null | undefined
  >(undefined)

  const toggleGroup = (supplierId: string | null) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(supplierId)) {
        next.delete(supplierId)
      } else {
        next.add(supplierId)
      }
      return next
    })
  }

  if (rfqLines.length === 0) {
    return null
  }

  const supplierCount = groups.filter((g) => g.supplier !== null).length
  const unassignedCount =
    groups.find((g) => g.supplier === null)?.lines.length ?? 0
  const linesWithoutSelectedActivities = rfqLines.filter(
    (line) => !(line.activities ?? []).some((activity) => activity.selected),
  ).length

  return (
    <div className={classes.root}>
      {/* Summary */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {rfqLines.length} items
        </Typography>
        {showSupplierAssignment && supplierCount > 0 && (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${supplierCount} RFQ${supplierCount > 1 ? 's' : ''} will be created`}
          />
        )}
        {showSupplierAssignment && unassignedCount > 0 && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`${unassignedCount} unassigned`}
          />
        )}
        {linesWithoutSelectedActivities > 0 && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`${linesWithoutSelectedActivities} item${linesWithoutSelectedActivities > 1 ? 's have' : ' has'} no selected activities`}
          />
        )}
      </Box>

      {/* Bulk assign */}
      {availableSuppliers?.length > 0 &&
        (onBulkAssignSupplier || onUnassignAll) && (
          <div className={classes.bulkAssign}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {selectionCount > 0
                ? `Assign supplier to ${selectionCount} selected:`
                : 'Assign supplier to all:'}
            </Typography>
            <Autocomplete
              size="small"
              options={availableSuppliers}
              getOptionLabel={(option) => option.name ?? ''}
              onChange={(_, value) => {
                if (value) onBulkAssignSupplier(value)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select supplier..."
                  variant="outlined"
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start !important',
                    gap: 0.25,
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.glNumber
                        ? `(${option.glNumber})`
                        : '(No GL number)'}
                    </Typography>
                  </span>
                  <Typography variant="caption" color="text.secondary">
                    {option.activities
                      ?.map((a) =>
                        getLocalizedWorkingStepName(a.workingStepType),
                      )
                      .join(' | ')}
                  </Typography>
                </Box>
              )}
              sx={{ minWidth: 280 }}
            />
            {onUnassignAll && supplierCount > 0 && (
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={onUnassignAll}
                sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
              >
                Unassign all
              </Button>
            )}
            {onDeleteSelected && selectionCount > 0 && (
              <Button
                size="small"
                variant="text"
                color="error"
                onClick={onDeleteSelected}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Remove selected ({selectionCount})
              </Button>
            )}
          </div>
        )}

      {/* Grouped sections */}
      {groups.map((group) => {
        const isCollapsed = collapsedGroups.has(group.supplierId)
        const isUnassigned = group.supplier === null
        const isChangingSupplier = changingSupplierFor === group.supplierId

        return (
          <Paper
            key={group.supplierId ?? 'unassigned'}
            variant="outlined"
            sx={{
              overflow: 'hidden',
              borderColor:
                showSupplierAssignment && isUnassigned
                  ? 'warning.main'
                  : undefined,
              borderStyle:
                showSupplierAssignment && isUnassigned ? 'dashed' : undefined,
            }}
          >
            {/* Group header */}
            <div
              className={classes.groupHeader}
              onClick={() => toggleGroup(group.supplierId)}
            >
              <ExpandMoreIcon
                fontSize="small"
                className={cx(
                  classes.expandIcon,
                  !isCollapsed && classes.expandIconOpen,
                )}
              />

              {!showSupplierAssignment ? (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  RFQ scope
                </Typography>
              ) : isUnassigned ? (
                <Typography
                  variant="body2"
                  color="warning.main"
                  sx={{ fontWeight: 600 }}
                >
                  Unassigned items
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {group.supplier?.name}
                </Typography>
              )}

              <Chip
                size="small"
                label={`${group.lines.length} item${group.lines.length !== 1 ? 's' : ''}`}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />

              {!isUnassigned && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="info"
                  label="1 RFQ"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}

              <div className={classes.groupActions}>
                {/* Change supplier button */}
                {onChangeGroupSupplier && !isUnassigned && (
                  <IconButton
                    size="small"
                    title="Change supplier for this group"
                    onClick={(e) => {
                      e.stopPropagation()
                      setChangingSupplierFor(
                        isChangingSupplier ? undefined : group.supplierId,
                      )
                    }}
                  >
                    <SwapHorizIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            </div>

            {/* Change supplier inline */}
            {isChangingSupplier && availableSuppliers?.length > 0 && (
              <Box sx={{ px: 2, pb: 1 }}>
                <Autocomplete
                  size="small"
                  options={availableSuppliers.filter(
                    (s) => s.supplierId !== group.supplierId,
                  )}
                  getOptionLabel={(option) => option.name ?? ''}
                  onChange={(_, value) => {
                    if (value) {
                      onChangeGroupSupplier(group.supplierId, value)
                      setChangingSupplierFor(undefined)
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Move items to different supplier..."
                      variant="outlined"
                      size="small"
                      autoFocus
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start !important',
                        gap: 0.25,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.activities
                          ?.map((a) =>
                            getLocalizedWorkingStepName(a.workingStepType),
                          )
                          .join(' · ')}
                      </Typography>
                    </Box>
                  )}
                  className={classes.changeSupplierInput}
                />
              </Box>
            )}

            {/* Lines table */}
            <Collapse in={!isCollapsed}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {onSelectionChange && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            indeterminate={
                              group.lines.some((l) =>
                                selectedBomItemIds?.has(l.bomItemPointer.id),
                              ) &&
                              !group.lines.every((l) =>
                                selectedBomItemIds?.has(l.bomItemPointer.id),
                              )
                            }
                            checked={
                              group.lines.length > 0 &&
                              group.lines.every((l) =>
                                selectedBomItemIds?.has(l.bomItemPointer.id),
                              )
                            }
                            onChange={(e) => {
                              const checked = e.target.checked
                              group.lines.forEach((l) => {
                                if (l.bomItemPointer.id) {
                                  onSelectionChange(
                                    l.bomItemPointer.id,
                                    checked,
                                  )
                                }
                              })
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell>Item</TableCell>
                      {onLineSupplierChange && <TableCell>Supplier</TableCell>}
                      <TableCell align="right">Activities</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.lines.map((line, index) => {
                      if (!line.bomItemPointer) {
                        return null
                      }

                      return (
                        <ProjectRFQLine
                          key={line.bomItemPointer.id || `rfq-line-${index}`}
                          bomItemPointer={line.bomItemPointer}
                          line={line}
                          index={index}
                          onSelectionChange={onSelectionChange}
                          selected={selectedBomItemIds?.has(
                            line.bomItemPointer.id,
                          )}
                          supplier={group.supplier ?? line.supplier}
                          availableSuppliers={
                            onLineSupplierChange
                              ? availableSuppliers
                              : undefined
                          }
                          onSupplierChange={onLineSupplierChange}
                          onActivitySelectionChange={
                            onLineActivitySelectionChange
                          }
                        />
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>
        )
      })}
    </div>
  )
}
